import { Camera, Vector3 } from "three";
import type { CameraConfig } from "../../cameraConfig";
import type { CollisionSystem } from "../collision/CollisionSystem";
import type { ViewMode } from "../../types";
import { FirstPersonControls } from "./FirstPersonControls";
import { OrbitCameraControls } from "./OrbitCameraControls";

export class SceneControls {
  onOrbitInteractionStart?: () => void;
  onOrbitInteractionEnd?: () => void;
  orbitAutoMove: boolean = false;
  orbitAutoMoveDirection: "left" | "right" = "left";
  orbitAutoMoveSpeed: number = 5;

  private _viewMode: ViewMode = "orbit";
  private _enabledState: boolean = true;
  private _orbitCenter: Vector3 = new Vector3();
  private _orbitTargetOverride: Vector3 | null = null;
  private _orbitReturnActive: boolean = false;
  private _orbitReturnElapsed: number = 0;
  private _orbitReturnDuration: number = 0.6;
  private _orbitReturnStartTarget: Vector3 = new Vector3();
  private _orbitReturnTarget: Vector3 = new Vector3();
  private _orbitAnimationActive: boolean = false;
  private _orbitReturnOnInteraction: boolean = true;
  private _onOrbitStart: () => void;
  private _onOrbitEnd: () => void;

  readonly firstPerson: FirstPersonControls;
  readonly orbit: OrbitCameraControls;

  constructor(object: Camera, domElement: HTMLElement | null = null) {
    this.firstPerson = new FirstPersonControls(object, domElement);
    this.orbit = new OrbitCameraControls(object, domElement);

    this._onOrbitStart = this.handleOrbitInteractionStart.bind(this);
    this._onOrbitEnd = this.handleOrbitInteractionEnd.bind(this);
    this.orbit.addEventListener("start", this._onOrbitStart);
    this.orbit.addEventListener("end", this._onOrbitEnd);

    if (domElement !== null) {
      this.connect(domElement);
      this.handleResize();
    }

    this.firstPerson.syncOrientationFromCamera();
    this.applyOrbitCenterTarget();
    this.updateEnabledState();
  }

  get viewMode(): ViewMode {
    return this._viewMode;
  }

  setViewMode(mode: ViewMode): void {
    if (this._viewMode === mode) {
      return;
    }

    this._viewMode = mode;
    if (mode === "orbit") {
      this.firstPerson.resetInputState();
      this.applyOrbitCenterTarget();
      this.firstPerson.lookAt(this._orbitCenter);
      this.orbit.syncPosition();
    } else {
      this.lookAt(this.orbit.target);
    }

    this.updateEnabledState();
  }

  get enabled(): boolean {
    return this._enabledState;
  }

  set enabled(value: boolean) {
    if (this._enabledState === value) {
      return;
    }
    this._enabledState = value;
    this.updateEnabledState();
    if (!value) {
      this.firstPerson.resetInputState();
    }
  }

  set collisionSystem(value: CollisionSystem | null) {
    this.firstPerson.collisionSystem = value;
    this.orbit.collisionSystem = value;
  }

  connect(element: HTMLElement): void {
    this.firstPerson.connect(element);
    this.orbit.connect(element);
    this.updateEnabledState();
  }

  disconnect(): void {
    this.firstPerson.disconnect();
    this.orbit.disconnect();
  }

  dispose(): void {
    this.disconnect();
    this.orbit.removeEventListener("start", this._onOrbitStart);
    this.orbit.removeEventListener("end", this._onOrbitEnd);
    this.firstPerson.dispose();
    this.orbit.dispose();
  }

  handleResize(): void {
    this.firstPerson.handleResize();
  }

  lookAt(x: number | Vector3, y?: number, z?: number): this {
    this.firstPerson.lookAt(x, y, z);

    if (this._viewMode === "first-person") {
      const target = x instanceof Vector3 ? x : new Vector3(x, y!, z!);
      this.orbit.setTarget(target);
    }

    return this;
  }

  update(delta: number): void {
    if (this._viewMode === "orbit") {
      if (this.enabled === false) {
        return;
      }

      if (this._orbitReturnActive) {
        this.orbit.setAutoRotate(false, 0);
        this._orbitReturnElapsed += delta;
        const progress = Math.min(
          this._orbitReturnElapsed / Math.max(this._orbitReturnDuration, 0.0001),
          1
        );
        const easedProgress = this.easeInOutCubic(progress);
        this._orbitReturnTarget.lerpVectors(
          this._orbitReturnStartTarget,
          this._orbitCenter,
          easedProgress
        );
        this.orbit.setTarget(this._orbitReturnTarget);
        if (progress >= 1) {
          this._orbitReturnActive = false;
          this._orbitTargetOverride = null;
        }
      }

      if (
        !this._orbitAnimationActive &&
        !this._orbitReturnActive &&
        this.orbitAutoMove &&
        this.orbitAutoMoveSpeed !== 0
      ) {
        const direction = this.orbitAutoMoveDirection === "left" ? 1 : -1;
        const angularSpeed = Math.abs(this.orbitAutoMoveSpeed);
        if (this.orbit.rotateLeft(direction * angularSpeed * delta)) {
          this.orbit.setAutoRotate(false, 0);
        } else {
          this.orbit.setAutoRotate(true, direction * angularSpeed);
        }
      } else {
        this.orbit.setAutoRotate(false, 0);
      }

      this.orbit.update();
      return;
    }

    this.firstPerson.update(delta);
  }

  setOrbitCenter(center: Vector3 | { x: number; y: number; z: number }): void {
    if (center instanceof Vector3) {
      this._orbitCenter.copy(center);
    } else {
      this._orbitCenter.set(center.x, center.y, center.z);
    }

    if (this._viewMode === "orbit") {
      this.firstPerson.lookAt(this._orbitCenter);
      this.applyOrbitCenterTarget();
    } else {
      this.orbit.setTarget(this._orbitCenter);
    }
  }

  applyCameraConfig(config: CameraConfig): void {
    this.orbitAutoMove = config.orbitAutoMove;
    this.orbitAutoMoveDirection = config.orbitAutoMoveDirection;
    this.orbitAutoMoveSpeed = config.orbitAutoMoveSpeed;

    this.firstPerson.lookSpeedHorizontal = config.lookSpeedHorizontal;
    this.firstPerson.lookSpeedVertical = config.lookSpeedVertical;
    this.firstPerson.dragInertia = config.dragInertia;
    this.firstPerson.movementSpeed = config.movementSpeed;
    this.firstPerson.autoForward = config.autoForward;
    this.firstPerson.activeLook = config.activeLook;
    this.firstPerson.heightSpeed = config.heightSpeed;
    this.firstPerson.heightCoef = config.heightCoef;
    this.firstPerson.speedHeightMin = config.speedHeightMin;
    this.firstPerson.speedHeightMax = config.speedHeightMax;
    this.firstPerson.pinchMoveSpeed = config.pinchMoveSpeed;
    this.firstPerson.lookVertical = config.lookVertical;
    this.firstPerson.constrainVertical = config.constrainVertical;
    this.firstPerson.verticalMin = config.verticalMin;
    this.firstPerson.verticalMax = config.verticalMax;
    this.firstPerson.clampMinHeight = config.clampMinHeight;
    this.firstPerson.clampMaxHeight = config.clampMaxHeight;

    this.orbit.minDistance = config.orbitMinDistance;
    this.orbit.maxDistance = config.orbitMaxDistance;
    this.orbit.minPolarAngle = config.orbitMinPolarAngle;
    this.orbit.maxPolarAngle = config.orbitMaxPolarAngle;
    this.orbit.clampMinHeight = config.clampMinHeight;
    this.orbit.clampMaxHeight = config.clampMaxHeight;
    this.orbit.clampMaxHeightFar = config.clampMaxHeightFar ?? config.clampMaxHeight;
    this.orbit.applyLimits();
  }

  setOrbitAnimationActive(active: boolean): void {
    this._orbitAnimationActive = active;
    if (active) {
      this.orbit.setAutoRotate(false, 0);
    }
  }

  setOrbitTargetOverride(target: Vector3): void {
    if (!this._orbitTargetOverride) {
      this._orbitTargetOverride = new Vector3();
    }
    this._orbitTargetOverride.copy(target);
    this._orbitReturnActive = false;
    this.orbit.setTarget(this._orbitTargetOverride);
  }

  clearOrbitTargetOverride(): void {
    if (!this._orbitTargetOverride) {
      return;
    }
    this._orbitTargetOverride = null;
    this._orbitReturnActive = false;
    this.applyOrbitCenterTarget();
  }

  private applyOrbitCenterTarget(): void {
    const target = this._orbitTargetOverride ?? this._orbitCenter;
    this.orbit.setTarget(target);
  }

  private updateEnabledState(): void {
    this.firstPerson.enabled = this._enabledState && this._viewMode === "first-person";
    this.orbit.enabled = this._enabledState && this._viewMode === "orbit";
  }

  private handleOrbitInteractionStart(): void {
    this.onOrbitInteractionStart?.();
    if (!this._orbitReturnOnInteraction) {
      return;
    }
    if (!this._orbitTargetOverride) {
      return;
    }
    this._orbitReturnStartTarget.copy(this.orbit.target);
    this._orbitReturnElapsed = 0;
    this._orbitReturnActive = true;
  }

  private handleOrbitInteractionEnd(): void {
    if (this._orbitReturnActive) {
      this._orbitReturnActive = false;
    }
    this.onOrbitInteractionEnd?.();
  }

  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }
}
